import { PageType } from "../types/PageType";
import { ZeroResultType } from "../types/ZeroResultType";

import { SessionStore } from "../uidispatcherapi/SessionStore";
import { AnonymousCartMetaStore } from "../uidispatcherapi/AnonymousCartMetaStore";
import { CustomerCartMetaStore } from "../uidispatcherapi/CustomerCartMetaStore";
import { trackStructuredEvent } from "../utils/TrackingUtil";
import { TrackingTags } from "../tracking/Tracking";
import { isTier12Customer } from "../stores/SessionStore";
import { PriceAndPromotionInfo } from "../types/PriceAndPromotionInfo";
import { BettyBundle, BettyBundleStore } from "../types/EvaluateArticleNew";
import { canNotBeAddedToBasket } from "../utils/BasketUtil";
import { getDetailLink, getShowsSummaryDnrInfo, getStoreAvailability } from "../utils/articleUtil";
import { Bundle } from "../types/articlesearch/Article";
import { PageComponent } from "./PageComponents";

export type LoggedInTrackingStatus = "logged-in" | "not-logged-in";

export type BasketChangeDirection = "increased" | "decreased";

export type BasketAddTrackingData = {
    // Largely consolidated across UIs
    originEventId: string;
    bettyBundleId: string;
    trackingId: string;
    amount: number; // How much was added
    amountBefore: number | undefined; // Amount in the basket before user added more
    pageType: PageType;
    loggedIn: LoggedInTrackingStatus;
    currency: string | undefined;
    direction: BasketChangeDirection | null; // Whether items were added or removed from the basket
    description: string | undefined;
    initial?: boolean; // Whether there were items in the basket before already
    subsysNumber: string | undefined;
    mgbNumber: string | undefined;
    pageComponent?: PageComponent;

    // Not so consolidated across UIs
    inContainer: boolean | null | undefined;
    isPromo: boolean | null | undefined;
    isOnList: boolean | null | undefined;
    isRecentlyOrdered: boolean | null | undefined;
    recentlyOrderedDateString: string | null | undefined;
    marketplace: boolean | undefined;
    offerId: string | null | undefined;
    brand: string; // For the PDP
    brandName: string; // For the Search Result Page
    variantName: string;
    categoryPath: string;
    category?: string;
    strokePrice: number | string | undefined;
    // Note: price and primaryPrice denote the same value, in fact they seem to have been
    //   used interchangeably in the past (and in a duplicated way for the PDP-add-to-basket tracking)
    price: number | undefined;
    primaryPrice: number | undefined;
    weight: number | null | undefined;
    unitSize: string;
    listPosition: number | null | undefined;
    listId: string | null | undefined;
    availability: string | null | undefined;
    ropoRank: any;
    showsSummaryDnrInfo: boolean | undefined;
};

export type GACoreProduct = {
    name: string | null | undefined;
    id: string;
    price: string | null | undefined;
    brand: string | undefined;
    variant: string;
    category: string;
    metric3: string | null | undefined;
    dimension62: string | null | undefined;
    dimension61: string | null | undefined;
    dimension42: string | undefined;
    metric6: number | null | undefined;
};

export type GAChangeBasketProduct = GACoreProduct & {
    quantity: number;
};

export type GAAddToCartAdditionalInfo = {
    event: "addToCart";
    ecommerce: {
        currencyCode: string | null | undefined;
        add: {
            products: GAChangeBasketProduct[];
        };
    };
};

export type GARemoveFromCartAdditionalInfo = {
    event: "removeFromCart";
    ecommerce: {
        currencyCode: string | null | undefined;
        remove: {
            products: GAChangeBasketProduct[];
        };
    };
};

export type GAPDPViewAdditionalInfo = {
    ecommerce: {
        currencyCode: string | null;
        detail: {
            actionField: {};
            products: [GACoreProduct];
        };
    };
};

export type GAAdditionalInfo = GAPDPViewAdditionalInfo | GAAddToCartAdditionalInfo | GARemoveFromCartAdditionalInfo;

export type SaleCycleInfo = {
    saleCycle: {
        loggedIn: boolean | null | undefined;
        pageType: string;
        items: {
            q: number;
            p: {
                name: string | undefined;
                id: string;
                costs: {
                    singleItem: string | 0 | null | undefined;
                    previous: number | string | null | undefined;
                };
                url: string | null | undefined;
                images: {
                    thumbnail: string | null | undefined;
                };
                brand: string;
                description: string | undefined;
                size: {
                    value: string | null;
                };
                inStock: boolean;
                currency: string | null | undefined;
            };
        }[];
    };
};

export function getLoggedInTrackingStatus(loggedIn: boolean | null | undefined): LoggedInTrackingStatus {
    return loggedIn ? "logged-in" : "not-logged-in";
}

export function getBasketChangeDirection(changeInValue: number | null | undefined) {
    const sanitizedValue = changeInValue ?? 0;
    if (!sanitizedValue) {
        return null;
    }
    return sanitizedValue > 0 ? "increased" : "decreased";
}

export function getIsWeightPerPieceArticle<
    GenericWeightPerPieceUom extends {
        uom?: string | null | undefined;
        value?: number | null | undefined;
    }
>(isWeightArticle: boolean | null | undefined, weightPerPiece: GenericWeightPerPieceUom | null | undefined): boolean {
    if (
        isWeightArticle &&
        weightPerPiece &&
        weightPerPiece?.uom &&
        (weightPerPiece?.value ?? undefined) !== undefined
    ) {
        return true;
    }
    return false;
}

export function getBasketAddGAProduct(
    bettyBundleId: string,
    mgbNumber: string | null | undefined,
    subsysNumber: string | undefined,
    description: string | null | undefined,
    variantName: string,
    brandName: string | undefined,
    articleFinalNetValue: number | null | undefined,
    strokePrice: number | string | undefined,
    currentValue: number,
    categoryPath: string,
    isWeightArticle: boolean,
    isweightPerPieceArticle: boolean,
    weight: number | null | undefined,
    unitSize: string
): GAChangeBasketProduct {
    let gaPrice = articleFinalNetValue;
    let gaStrokePrice = typeof strokePrice === "string" ? parseFloat(strokePrice) : strokePrice;
    let gaQuantity = currentValue;
    if (isWeightArticle && unitSize?.toLowerCase() === "kg" && !isweightPerPieceArticle) {
        if (gaPrice) {
            gaPrice /= 1000;
        }
        if (gaStrokePrice) {
            gaStrokePrice /= 1000;
        }
        gaQuantity *= 1000;
    }
    const gaProduct: GAChangeBasketProduct = {
        name: description,
        id: bettyBundleId,
        price: gaPrice?.toString(),
        brand: brandName,
        variant: variantName,
        quantity: gaQuantity,
        category: categoryPath,
        metric3: gaStrokePrice ? gaStrokePrice.toString() : undefined,
        dimension62: unitSize,
        dimension61: mgbNumber,
        dimension42: subsysNumber,
        metric6: weight
    };
    return gaProduct;
}

export function getBasketAddGAAdditionalInfo(
    bettyBundleId: string,
    mgbNumber: string | undefined,
    subsysNumber: string | undefined,
    description: string | undefined,
    variantName: string,
    brandName: string,
    articleFinalNetValue: number | null | undefined,
    strokePrice: number | string | undefined,
    currency: string | null | undefined,
    currentValue: number,
    categoryPath: string,
    isWeightArticle: boolean,
    isweightPerPieceArticle: boolean,
    weight: number | null | undefined,
    unitSize: string
): GAAdditionalInfo {
    const gaProduct = getBasketAddGAProduct(
        bettyBundleId,
        mgbNumber,
        subsysNumber,
        description,
        variantName,
        brandName,
        articleFinalNetValue,
        strokePrice,
        currentValue,
        categoryPath,
        isWeightArticle,
        isweightPerPieceArticle,
        weight,
        unitSize
    );

    const gaAdditionalInfo: GAAdditionalInfo = {
        event: "addToCart",
        ecommerce: {
            currencyCode: currency,
            add: {
                products: [gaProduct]
            }
        }
    };
    return gaAdditionalInfo;
}

export function getBasketAddSaleCycleInfo(
    basketAddTrackingTags: BasketAddTrackingData,
    articleFinalNetValue: number | undefined,
    loggedIn: boolean,
    url: string | undefined,
    imageSrc: string | undefined,
    articleDeprecated: boolean,
    cartEnabled: boolean,
    selectedBundle: BettyBundle | undefined,
    selectedStoreData: BettyBundleStore | null,
    deliveryMode: "PICKUP" | "METRO_DELIVERY" | "PARCEL_DELIVERY" | "EXPRESS_DELIVERY" | "STORE" | undefined
): SaleCycleInfo {
    const productPrice = articleFinalNetValue && articleFinalNetValue?.toString();

    const disabled = canNotBeAddedToBasket(!!articleDeprecated, !!selectedBundle, !!cartEnabled, selectedStoreData);

    const saleCycleInfo: SaleCycleInfo = {
        saleCycle: {
            loggedIn: loggedIn,
            pageType: "Basket",
            items: [
                {
                    q: basketAddTrackingTags.amount,
                    p: {
                        name: basketAddTrackingTags.description,
                        id: basketAddTrackingTags.bettyBundleId,
                        costs: {
                            singleItem: productPrice,
                            previous: basketAddTrackingTags.strokePrice
                        },
                        url: url,
                        images: {
                            thumbnail: imageSrc
                        },
                        brand: basketAddTrackingTags.brandName,
                        description: basketAddTrackingTags.description,
                        size: {
                            value: basketAddTrackingTags.unitSize
                        },
                        inStock: !disabled && !(deliveryMode === "STORE"),
                        currency: basketAddTrackingTags.currency
                    }
                }
            ]
        }
    };
    return saleCycleInfo;
}

export type BasketAddTrackingTags = {
    // Originally coming from PDP code
    originEventId?: string;
    pageComponent?: PageComponent;
    value?: number; // The amount in basket after basket add
    pageType?: PageType;
    loggedIn?: LoggedInTrackingStatus;
    bettyBundleId?: string;
    description?: string;
    imageUrl?: string;
    brand?: string;
    variantText?: string;
    customerDisplayId?: string;
    weightArticle?: boolean;
    isWeightPerPieceArticle?: boolean;
    amountBefore?: number;
    ownBrand?: string;
    trackingId?: string;
    price?: string | number;
    strokePrice?: string | number;

    // Not readily accessible everywhere (coming from PDP code)
    availablePromotions?: string;
    customerBuyable?: boolean;
    employeeBuyable?: boolean;
    availability?: string;

    // Probably specific to Alternatives
    position?: string | number;
    requestBundleId?: string;
    colaTraceId?: string;
} & TrackingTags;

type NeededInfo = {
    // TODO / FIXME: More of the "NeededInfo" type should be
    //   moved directly into the "NeededInfo.trackingTags" object!
    originEventId: string;
    value: number;
    amount: number;
    bettyBundleId: string;
    correctDelta: number;
    trackingId: string;
    articleDeprecated: boolean;
    cartEnabled: boolean;
    isLoggedIn: boolean;
    isEmployee: boolean | null;
    selectedStoreId: string | null | undefined;
    inContainer?: boolean;
    recentlyOrdered?: boolean;
    recentlyOrderedDateString?: string;
    pageType: string;
    zeroResultType?: string | undefined | null;
    amountBefore: number | undefined | null;

    // TODO: Tracking info is already prepared to take general "TrackingTags",
    //   thus trackingInfo object shoould be integrated into trackingTags object
    //   (which simply has a stronger, hence better, typing than trackingInfo).
    trackingInfo: TrackingTags | null | undefined;
    trackingTags: BasketAddTrackingTags;
};

function removeUndesiredTrackingTags(aggregatedTrackingData: TrackingTags) {
    delete aggregatedTrackingData?.amountInBasket;
    delete aggregatedTrackingData?.recentlyOrdered;
    delete aggregatedTrackingData?.fetchedArticle;
    delete aggregatedTrackingData?.variantText;
    delete aggregatedTrackingData?.isLoggedIn;
    delete aggregatedTrackingData?.categoryIds;
    delete aggregatedTrackingData?.title;
    delete aggregatedTrackingData?.promo;
}

export function getProcessedBasketAddTrackingData(
    props: NeededInfo,
    getBundleById?: ((bettyBundleId: string) => BettyBundle) | null,
    getPriceAndPromotionInfoById?: ((bettyBundleId: string) => PriceAndPromotionInfo) | null
) {
    function getFromTrackingDataObjects(key: string) {
        return props?.trackingInfo?.[key] ?? props?.trackingTags?.[key] ?? undefined;
    }

    // Note: For some places, where this function is used, the bundle/price data is simply not present.
    //   Therefore, we have these callbacks in place. However, if these are not provided, it is assumed
    //   that the corresponding article data comes via props.trackingTags already!
    // TODO: If possible, better replace handing through these callbacks by handing through the actual
    //   bundle and corresponding priceAndPromotionInfo; That's easier to follow in terms of code.
    const selectedBundle = getBundleById ? getBundleById(props.bettyBundleId) : undefined;
    const priceAndPromotionInfo = getPriceAndPromotionInfoById
        ? getPriceAndPromotionInfoById(props.bettyBundleId)
        : undefined;

    const selectedStoreData =
        selectedBundle?.stores && props.selectedStoreId ? selectedBundle?.stores[props.selectedStoreId] : null;
    const isWeightArticle =
        selectedBundle?.isWeightArticle === "WEIGHT" ?? getFromTrackingDataObjects("isWeightArticle");
    const weightPerPiece = selectedBundle?.weightPerPiece ?? getFromTrackingDataObjects("weightPerPiece");
    const weight =
        isWeightArticle && weightPerPiece?.value
            ? weightPerPiece.value
            : getFromTrackingDataObjects("weightPerPiece") ?? getFromTrackingDataObjects("weight");
    const isweightPerPieceArticle = isWeightArticle && weightPerPiece;
    const unitSize =
        isWeightArticle && weightPerPiece?.uom
            ? weightPerPiece.uom
            : getFromTrackingDataObjects("uom") || getFromTrackingDataObjects("unit");
    const strokePrice =
        priceAndPromotionInfo?.strikeThroughNet ??
        getFromTrackingDataObjects("strikeThroughNet") ??
        getFromTrackingDataObjects("strokePrice");
    // It is predominantly called variantText in the rest of the code, but here we track it as variantname
    const variantName =
        getFromTrackingDataObjects("variantName") || getFromTrackingDataObjects("variantText") || "no-variant";
    const categoryPath =
        getFromTrackingDataObjects("category") ||
        getFromTrackingDataObjects("categoryPath") ||
        getFromTrackingDataObjects("categoryIds") ||
        "no-category";
    const brandName =
        selectedBundle?.brandName || getFromTrackingDataObjects("brandName") || getFromTrackingDataObjects("brand"); // Just to be on the safe side
    const subsysNumber =
        selectedBundle?.refBundleIds?.[0]?.subsystemArticleNumber ?? getFromTrackingDataObjects("subsysNumber");
    const mgbNumber = selectedBundle?.refBundleIds?.[0]?.mgbBundleNumber ?? getFromTrackingDataObjects("mgbNumber");
    const availability =
        (
            selectedBundle &&
            selectedStoreData &&
            getStoreAvailability(selectedBundle as Bundle, selectedStoreData, props.isEmployee ?? false)
        )?.availability ?? getFromTrackingDataObjects("availability");
    const description =
        selectedBundle?.description ??
        getFromTrackingDataObjects("description") ??
        getFromTrackingDataObjects("title") ??
        undefined;
    let pvLink;
    try {
        pvLink = selectedBundle && getDetailLink(selectedBundle as Bundle, null);
    } catch (error: unknown) {
        // Ignore: Facing type incompatabilities, apparently e.g. Bundle representations in Alternatives view cannot cope with this
    }
    const imageUrls = {
        bundle: selectedBundle?.imageUrl,
        bundleFallBack: selectedBundle?.fallbackImageUrl
    };

    const providedTrackingData = {
        ...(props.trackingInfo || {}),
        ...(props.trackingTags || {})
    };

    const primaryPrice =
        getFromTrackingDataObjects("price") ??
        getFromTrackingDataObjects("primaryPrice") ??
        getFromTrackingDataObjects("unitNet") ??
        null;

    const url = pvLink?.pathname ?? getFromTrackingDataObjects("pvLink");
    const imageSrc = imageUrls.bundle || imageUrls.bundleFallBack || getFromTrackingDataObjects("imageUrl");

    const curatedTrackingTags: BasketAddTrackingData = {
        bettyBundleId: props.bettyBundleId,
        originEventId: props.originEventId ?? getFromTrackingDataObjects("originEventId") ?? "",
        trackingId: props.trackingId || getFromTrackingDataObjects("trackingId") || "",
        amountBefore: props.amountBefore ?? getFromTrackingDataObjects("amountBefore"),
        initial: !props.amountBefore,
        inContainer: props?.inContainer ?? getFromTrackingDataObjects("inContainer"),
        isPromo:
            priceAndPromotionInfo?.isPromotion ??
            getFromTrackingDataObjects("isPromotion") ??
            getFromTrackingDataObjects("promo"),
        isOnList: getFromTrackingDataObjects("onList"),
        isRecentlyOrdered:
            props.recentlyOrdered ??
            getFromTrackingDataObjects("recentlyOrdered") ??
            getFromTrackingDataObjects("isRecentlyOrdered"),
        recentlyOrderedDateString:
            props.recentlyOrderedDateString || getFromTrackingDataObjects("recentlyOrderedDateString") || undefined,
        marketplace: getFromTrackingDataObjects("marketplace"),
        offerId: getFromTrackingDataObjects("offerId"),
        brand: brandName || "no-brand",
        brandName: brandName || "no-brand",
        variantName: variantName,
        // Tracked on old article card, e.g. in shopping lists:
        categoryPath: categoryPath,
        // Tracked on PDP:
        category: categoryPath,
        strokePrice: strokePrice || "no-strokePrice",
        price: primaryPrice,
        primaryPrice: primaryPrice,
        subsysNumber: subsysNumber || undefined,
        mgbNumber: mgbNumber || undefined,
        weight: weight ?? null,
        unitSize: unitSize || "no-unit-size",
        listPosition: getFromTrackingDataObjects("listPosition") || undefined,
        listId: getFromTrackingDataObjects("listId") || undefined,
        availability: availability || undefined,
        amount: props.amount,
        ropoRank: getFromTrackingDataObjects("ropoRank"),
        description,
        pageType: (props.pageType || getFromTrackingDataObjects("pageType") || "unknown") as PageType,
        loggedIn: getLoggedInTrackingStatus(props.isLoggedIn),
        currency: priceAndPromotionInfo?.currency || getFromTrackingDataObjects("currency") || undefined,
        direction: getBasketChangeDirection(props.amount),
        showsSummaryDnrInfo:
            (priceAndPromotionInfo && getShowsSummaryDnrInfo(priceAndPromotionInfo)) ??
            getFromTrackingDataObjects("showsSummaryDnrInfo") ??
            undefined
    };

    const aggregatedTrackingData: BasketAddTrackingData & TrackingTags = Object.assign(
        {},
        providedTrackingData,
        curatedTrackingTags
    );

    removeUndesiredTrackingTags(aggregatedTrackingData);

    const gaAdditionalInfo = getBasketAddGAAdditionalInfo(
        aggregatedTrackingData.bettyBundleId,
        aggregatedTrackingData.mgbNumber,
        aggregatedTrackingData.subsysNumber,
        aggregatedTrackingData.description,
        aggregatedTrackingData.variantName,
        aggregatedTrackingData.brandName,
        priceAndPromotionInfo?.unitNet ?? aggregatedTrackingData?.price ?? aggregatedTrackingData?.primaryPrice,
        aggregatedTrackingData.strokePrice,
        aggregatedTrackingData.currency,
        aggregatedTrackingData.amount,
        aggregatedTrackingData.categoryPath,
        isWeightArticle,
        isweightPerPieceArticle,
        aggregatedTrackingData.weight,
        aggregatedTrackingData.unitSize
    );

    const deliveryMode =
        selectedStoreData?.selectedDeliveryMode ??
        getFromTrackingDataObjects("selectedDeliveryMode") ??
        getFromTrackingDataObjects("deliveryMode");

    const storeForSaleCycle: Partial<BettyBundleStore> = selectedStoreData ?? {
        customerBuyable: getFromTrackingDataObjects("customerBuyable") as boolean | undefined,
        employeeBuyable: getFromTrackingDataObjects("employeeBuyable") as boolean | undefined
    };

    const saleCycleInfo = getBasketAddSaleCycleInfo(
        aggregatedTrackingData,
        primaryPrice,
        props.isLoggedIn,
        url,
        imageSrc,
        props.articleDeprecated,
        props.cartEnabled,
        selectedBundle,
        storeForSaleCycle,
        deliveryMode
    );

    return {
        trackingTags: aggregatedTrackingData,
        gaAdditionalInfo,
        saleCycleInfo
    };
}

export function computePropsAndTrackBasketChange(
    props: NeededInfo,
    getBundleById: ((bettyBundleId: string) => BettyBundle) | null,
    getPriceAndPromotionInfoById: ((bettyBundleId: string) => PriceAndPromotionInfo) | null
) {
    const processedtrackingData = getProcessedBasketAddTrackingData(props, getBundleById, getPriceAndPromotionInfoById);

    if (!processedtrackingData) {
        return;
    }

    const { trackingTags, gaAdditionalInfo, saleCycleInfo } = processedtrackingData;

    trackAddToBasket(
        trackingTags.originEventId,
        trackingTags.pageType,
        trackingTags.zeroResultType,
        trackingTags.bettyBundleId,
        trackingTags.trackingId,
        trackingTags.value, // TODO: this seems to be missing/unhandled yet?
        trackingTags.articleFinalNetValue,
        trackingTags.currency,
        trackingTags,
        gaAdditionalInfo,
        saleCycleInfo,
        trackingTags?.ownBrand,
        trackingTags?.showsSummaryDnrInfo
    );
    trackBasketChange(
        trackingTags.originEventId,
        trackingTags.pageType,
        trackingTags.bettyBundleId,
        trackingTags.trackingId,
        trackingTags.value,
        trackingTags.direction,
        trackingTags
    );
}

export function trackAddToBasket(
    originEventId: string,
    pageType: PageType,
    zeroResultType: ZeroResultType | undefined | null,
    bettyBundleId: string,
    trackingId: string,
    value: number,
    primaryPrice: number | undefined | null,
    currency: string | undefined | null,
    additionalInfo: BasketAddTrackingData | undefined | null,
    gaAdditionalInfo: GAAdditionalInfo | undefined | null,
    saleCycleInfo: SaleCycleInfo | undefined | null,
    ownBrand: string | undefined | null,
    showsSummaryDnrInfo: boolean | null | undefined
) {
    const tags: TrackingTags = Object.assign({}, additionalInfo || {}, {
        cartId: getCartId(),
        originEventId,
        pageType,
        zeroResultType,
        bettyBundleId,
        trackingId,
        value,
        primaryPrice: primaryPrice || additionalInfo?.primaryPrice || additionalInfo?.price || null,
        currency: currency || undefined,
        ownBrand: ownBrand || undefined,
        showsSummaryDnrInfo
    });

    const hiddenTags = Object.assign({}, gaAdditionalInfo || {}, saleCycleInfo || {});
    trackStructuredEvent("basket.add", { tags: tags }, hiddenTags);
}

// TODO: This doesn't seem to be used anywhere
export function trackBasketChange(
    originEventId: string,
    pageType: PageType,
    bettyBundleId: string,
    trackingId: string,
    value: number,
    direction: BasketChangeDirection | null,
    additionalInfo: BasketAddTrackingData | undefined | null
) {
    const tags: TrackingTags = Object.assign({}, additionalInfo || {}, {
        cartId: getCartId(),
        originEventId,
        pageType,
        bettyBundleId,
        trackingId,
        value,
        direction
    });
    trackStructuredEvent("basket.change", { tags: tags });
}

export function trackRemoveFromBasket(
    originEventId: string,
    pageType: PageType,
    bettyBundleId: string,
    trackingId: string,
    value: number,
    additionalInfo: BasketAddTrackingData | undefined | null,
    gaAdditionalInfo: GAAdditionalInfo | undefined | null,
    saleCycleInfo: SaleCycleInfo | undefined | null
) {
    const tags: TrackingTags = Object.assign({}, additionalInfo || {}, {
        cartId: getCartId(),
        originEventId,
        pageType,
        bettyBundleId,
        trackingId,
        value
    });
    const hiddenTags = Object.assign({}, gaAdditionalInfo || {}, saleCycleInfo || {});
    trackStructuredEvent("basket.remove", { tags: tags }, hiddenTags);
}

export function trackAddAllItemsToCart(
    originEventId: string | undefined | null,
    pageType: PageType,
    articleIds: string[],
    totalValue?: number | undefined,
    currency?: string | undefined,
    missingArticleIds?: string[],
    additionalInfo?:
        | {
              [x: string]: any;
          }
        | undefined
        | null
) {
    const tags: TrackingTags = Object.assign({}, additionalInfo || {}, {
        cartId: getCartId(),
        originEventId,
        pageType,
        articleIds,
        totalValue,
        currency,
        missingArticleIds
    });
    trackStructuredEvent("basket.addAll", { tags: tags });
}

const getCartId = () => {
    return SessionStore.loggedIn() && !isTier12Customer()
        ? CustomerCartMetaStore.getCartId()
        : AnonymousCartMetaStore.getCartId();
};
